import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Form, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal } from "../../../Common/DeleteModal";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import { toCdnUrl } from "../../../api/fileUploadService";
import { deleteVideo, listVideos, SflTvVideo } from "../../../api/tournament/videoService";
import { ActionMenu } from "../../../components/Ads/ActionMenu/ActionMenu";
import Pagination from "../../ShopServices/Orders/components/Pagination";

import "../../Ads/Creatives/Creatives.css";
import "../../Ads/Creatives/components/CreativeTable.css";
import "./Videos.css";

const PAGE_SIZE = 10;

interface VideosTabProps {
  tournamentId: string;
  seasonId: string;
  returnPath: string;
  parentState?: Record<string, unknown>;
}

const VideosTab: React.FC<VideosTabProps> = ({ tournamentId, seasonId, returnPath, parentState }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<SflTvVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<SflTvVideo | null>(null);
  const [deleting, setDeleting] = useState(false);

  const navigationState = { activeTab: "videos", returnPath, ...parentState };
  const basePath = `/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}/videos`;

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVideos(await listVideos({
        tournament: tournamentId,
        tournamentSeason: seasonId,
        ...(active === "" ? {} : { isActive: active === "true" }),
      }));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId, seasonId, active]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const filtered = useMemo(() => videos.filter((video) =>
    [video.title, video.subtitle, video.section, video.date, video.type, video.category, video.venue]
      .filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  ), [videos, search]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [search, active]);

  const remove = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await deleteVideo(selected._id);
      setSelected(null);
      toast.success("Video deleted successfully.");
      await fetchVideos();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const open = (path: string) => navigate(path, { state: navigationState });

  return (
    <>
      <Row><Col><Card className="ads-page-card"><Card.Body className="ads-page-card__body">
        <div className="ads-toolbar">
          <div className="ads-searchbar">
            <div className="ads-searchbar__icon"><i className="ph-magnifying-glass" /></div>
            <input className="ads-searchbar__input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search videos" aria-label="Search videos" />
            {search && <button type="button" className="ads-searchbar__clear" onClick={() => setSearch("")} aria-label="Clear search"><i className="ph-x" /></button>}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Form.Select className="sfltv-status-select" value={active} onChange={(e) => setActive(e.target.value)} aria-label="Filter video activity">
              <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
            </Form.Select>
            <button type="button" className="ads-create-btn" onClick={() => open(`${basePath}/create`)}><i className="ph-plus" /> Create Video</button>
          </div>
        </div>
        {loading ? <ShimmerLoader type="table" rows={6} /> : visible.length ? <div className="ads-table-wrapper"><Table responsive className="ads-table">
          <thead><tr><th>Video</th><th>Section</th><th>Date</th><th>Type</th><th>Category</th><th>Status</th><th>Active</th><th>Order</th><th>Actions</th></tr></thead>
          <tbody>{visible.map((video) => <tr key={video._id}>
            <td><div className="d-flex align-items-center gap-2">{video.thumbnailUrl ? <img className="ads-creative-thumb" src={toCdnUrl(video.thumbnailUrl)} alt="" /> : <div className="ads-creative-thumb ads-creative-thumb--placeholder"><i className="ph-video" /></div>}<div><strong>{video.title}</strong><small>{video.subtitle || video.venue || "-"}</small></div></div></td>
            <td>{video.section || "-"}</td><td>{video.date}</td><td>{video.type === "live_stream" ? "Live stream" : "Highlight"}</td><td>{video.category || "-"}</td><td>{video.liveStreamInfo?.status || video.manualStatus || "-"}</td>
            <td><span className={`ads-tag ${video.isActive ? "ads-tag--active" : "ads-tag--archived"}`}>{video.isActive ? "Active" : "Inactive"}</span></td><td>{video.sortOrder}</td>
            <td><ActionMenu size="sm" ariaLabel={`Actions for ${video.title}`} items={[
              { key: "view", label: "View", iconClassName: "ph-eye", onSelect: () => open(`${basePath}/${video._id}`) },
              { key: "edit", label: "Edit", iconClassName: "ph-pencil", onSelect: () => open(`${basePath}/${video._id}/edit`) },
              { key: "delete", label: "Delete", iconClassName: "ph-trash", isDanger: true, onSelect: () => setSelected(video) },
            ]} /></td>
          </tr>)}</tbody>
        </Table></div> : <div className="text-center py-5 text-muted">No videos found for this season.</div>}
      </Card.Body></Card>
      {!loading && filtered.length > PAGE_SIZE && <Pagination currentPage={page} totalPages={pages} totalCount={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} onPageSizeChange={() => {}} />}
      </Col></Row>
      {error && <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>{error}</Alert>}
      <DeleteModal show={!!selected} handleClose={() => setSelected(null)} deleteModalFunction={remove} isLoading={deleting} title="Delete video?" message="This permanently deletes this video and cannot be undone." confirmLabel="Delete" />
    </>
  );
};

export default VideosTab;
